/**
 * Generic reference-entity manager (§8.1 shared pattern): searchable table,
 * Active filter, modal create/edit with per-field help tooltips, SuperAdmin
 * delete with AlertDialog, CSV export. Driven by entityConfigs.js — the
 * versioned entities (profiles, margins) have their own managers.
 */

import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import Papa from 'papaparse';
import { payrollApi } from '../api';

function FieldInput({ field, value, onChange }) {
  const v = value === undefined || value === null ? '' : value;
  switch (field.type) {
    case 'boolean':
      return (
        <Checkbox isChecked={!!value} onChange={(e) => onChange(e.target.checked)}>
          {field.checkboxLabel || 'Yes'}
        </Checkbox>
      );
    case 'number':
      // Keep the RAW string while typing — parsing per keystroke eats
      // intermediate states like "1." so decimals were impossible to enter.
      // coerceNumericFields() converts to numbers at save time.
      return (
        <NumberInput size="sm" value={v} onChange={(nv) => onChange(nv === '' ? null : nv)}>
          <NumberInputField />
        </NumberInput>
      );
    case 'select':
      return (
        <Select size="sm" value={v} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}>
          <option value="">{field.placeholder || '—'}</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </Select>
      );
    case 'date':
      return <Input size="sm" type="date" value={v} onChange={(e) => onChange(e.target.value || null)} />;
    case 'textarea':
      return <Textarea size="sm" value={v} onChange={(e) => onChange(e.target.value)} rows={2} />;
    default:
      return <Input size="sm" value={v} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />;
  }
}

/**
 * Convert number-typed fields from their as-typed strings to real numbers.
 * Returns { values, badFields } — badFields lists labels that don't parse.
 */
export function coerceNumericFields(fields, form) {
  const values = { ...form };
  const badFields = [];
  fields.forEach((f) => {
    if (f.type !== 'number') return;
    const raw = values[f.key];
    if (raw === '' || raw === null || raw === undefined) { values[f.key] = null; return; }
    const num = Number(String(raw).trim());
    if (Number.isNaN(num)) badFields.push(f.label);
    else values[f.key] = num;
  });
  return { values, badFields };
}

export function EditorFields({ fields, form, setForm }) {
  return (
    <Flex direction="column" gap={3}>
      {fields.map((f) => (
        <FormControl key={f.key} isRequired={!!f.required}>
          <FormLabel fontSize="sm" mb={1}>
            {f.help ? (
              <Tooltip label={f.help} hasArrow placement="top" maxW="340px">
                <Text as="span" borderBottom="1px dotted" cursor="help">{f.label}</Text>
              </Tooltip>
            ) : f.label}
          </FormLabel>
          <FieldInput field={f} value={form[f.key]} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} />
        </FormControl>
      ))}
    </Flex>
  );
}

export default function EntityManager({ config, isSuperAdmin }) {
  const toast = useToast();
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const cancelRef = useRef();

  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null); // null | { row?, form }
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null); // row
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = async () => {
    setLoadError(null);
    try {
      const { rows: data } = await payrollApi.manageReference({ entity: config.entity, action: 'list' });
      setRows(data);
    } catch (err) {
      setLoadError(err.message);
    }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [config.entity]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (!showInactive && r.active === false) return false;
      if (!q) return true;
      return config.searchKeys.some((k) => String(r[k] || '').toLowerCase().includes(q));
    });
  }, [rows, search, showInactive, config.searchKeys]);

  const openCreate = () => {
    const form = {};
    config.fields.forEach((f) => { if (f.default !== undefined) form[f.key] = f.default; });
    setEditing({ row: null, form });
  };
  const openEdit = (row) => {
    const form = {};
    config.fields.forEach((f) => { form[f.key] = row[f.key]; });
    setEditing({ row, form });
  };

  const save = async () => {
    const missing = config.fields.filter((f) => f.required && (editing.form[f.key] === undefined || editing.form[f.key] === null || editing.form[f.key] === ''));
    if (missing.length) {
      toast({ title: `Missing: ${missing.map((f) => f.label).join(', ')}`, status: 'warning', duration: 4000 });
      return;
    }
    const { values, badFields } = coerceNumericFields(config.fields, editing.form);
    if (badFields.length) {
      toast({ title: `Not a number: ${badFields.join(', ')}`, status: 'warning', duration: 4000 });
      return;
    }
    setSaving(true);
    try {
      await payrollApi.manageReference({
        entity: config.entity,
        action: editing.row ? 'update' : 'create',
        id: editing.row ? editing.row.id : undefined,
        payload: values,
      });
      toast({ title: editing.row ? 'Updated' : 'Created', status: 'success', duration: 3000 });
      setEditing(null);
      await load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 8000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setDeleteBusy(true);
    try {
      await payrollApi.manageReference({ entity: config.entity, action: 'delete', id: deleting.id, reason: 'deleted via Reference UI' });
      toast({ title: 'Deleted', status: 'success', duration: 3000 });
      setDeleting(null);
      await load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 8000, isClosable: true });
    } finally {
      setDeleteBusy(false);
    }
  };

  const exportCsv = () => {
    const csv = Papa.unparse(filtered.map((r) => {
      const out = {};
      config.tableCols.forEach((c) => { out[c.label] = r[c.key]; });
      return out;
    }));
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `payroll-${config.entity}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (loadError) return <Alert status="error" borderRadius="md"><AlertIcon />{loadError}<Button ml={3} size="sm" onClick={load}>Retry</Button></Alert>;
  if (!rows) return <Flex justify="center" py={10}><Spinner /></Flex>;

  return (
    <Box>
      <Flex gap={2} mb={3} wrap="wrap" align="center">
        <Input size="sm" maxW="260px" placeholder={`Search ${config.title.toLowerCase()}…`}
          value={search} onChange={(e) => setSearch(e.target.value)} />
        {config.hasActive && (
          <Checkbox size="sm" isChecked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}>
            show inactive
          </Checkbox>
        )}
        <Box flex="1" />
        <Button size="sm" variant="outline" onClick={exportCsv}>Export CSV</Button>
        <Button size="sm" colorScheme="purple" onClick={openCreate}>New</Button>
      </Flex>

      {config.description && <Text fontSize="xs" color="gray.500" mb={2}>{config.description}</Text>}

      <Box overflowX="auto" borderWidth="1px" borderRadius="md">
        <Table size="sm">
          <Thead>
            <Tr>
              {config.tableCols.map((c) => <Th key={c.key} isNumeric={c.isNumeric}>{c.label}</Th>)}
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {filtered.length === 0 && (
              <Tr><Td colSpan={config.tableCols.length + 1}><Text py={3} textAlign="center" color="gray.500">Nothing here.</Text></Td></Tr>
            )}
            {filtered.map((r) => (
              <Tr key={r.id} _hover={{ bg: hoverBg }}>
                {config.tableCols.map((c) => (
                  <Td key={c.key} isNumeric={c.isNumeric} maxW={c.maxW} isTruncated={!!c.maxW}
                    title={c.maxW ? String(r[c.key] ?? '') : undefined}>
                    {c.render ? c.render(r) : (r[c.key] === null || r[c.key] === undefined || r[c.key] === '' ? '—' : String(r[c.key]))}
                  </Td>
                ))}
                <Td textAlign="right" whiteSpace="nowrap">
                  <Button size="xs" variant="outline" mr={1} onClick={() => openEdit(r)}>Edit</Button>
                  {isSuperAdmin && (
                    <Button size="xs" colorScheme="red" variant="ghost" onClick={() => setDeleting(r)}>Delete</Button>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* editor */}
      <Modal isOpen={!!editing} onClose={() => setEditing(null)} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing && editing.row ? `Edit ${config.singular}` : `New ${config.singular}`}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editing && <EditorFields fields={config.fields} form={editing.form} setForm={(fn) => setEditing((s) => ({ ...s, form: typeof fn === 'function' ? fn(s.form) : fn }))} />}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setEditing(null)}>Cancel</Button>
            <Button colorScheme="purple" onClick={save} isLoading={saving}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* delete confirm */}
      <AlertDialog isOpen={!!deleting} leastDestructiveRef={cancelRef} onClose={() => setDeleting(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete {config.singular}?</AlertDialogHeader>
            <AlertDialogBody>
              This permanently removes it from the reference data (the audit log keeps a copy).
              Prefer marking it inactive unless it was created in error.
              {deleting && <Badge display="block" mt={2}>{config.searchKeys.map((k) => deleting[k]).filter(Boolean).join(' · ')}</Badge>}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleting(null)}>Cancel</Button>
              <Button colorScheme="red" ml={2} onClick={confirmDelete} isLoading={deleteBusy}>Delete</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
